import { useCallback, useState } from 'react';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { useDispatch, useSelector } from 'react-redux';
import { clearDependencies } from '../../../reducers/documentation/documentationReducer';
import useParser from '../../parser/useParser';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { useDocumentationContext } from '../context/DocumentationContext';
import { useDocumentation } from '../useDocumentation';

export const useObjectListInteractions = () => {
  const dispatch = useDispatch();
  const { dispatchSingleObjectName } = useDocumentation();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);

  const { parseObjectOnDemand } = useParser({ crmOrgId: crmOrgId ?? '' });
  const { objectNamesUsedInCanvas } = useDocumentationContext();

  const [isLoading, setIsLoading] = useState(false);

  const onObjectNotInFunnelMapClick = useCallback(
    async (objectTypeName: ObjectTypeName) => {
      // If no record types are converted for given object, no configuration items (validationRules, apex etc..) are being stored on BE,
      // In order to get them, additional parse call is required and the response of this call is NOT being stored as well
      await parseObjectOnDemand(objectTypeName.objectType);
      dispatchSingleObjectName({ singleObjectName: objectTypeName });
    },
    [parseObjectOnDemand, dispatchSingleObjectName],
  );

  const onObjectInFunnelMapClick = useCallback(
    (objectTypeName: ObjectTypeName, tab?: DocumentationTabTypes) => {
      dispatchSingleObjectName({ singleObjectName: objectTypeName, tab });
    },
    [dispatchSingleObjectName],
  );

  const _onAllObjectsCardClick = useCallback(
    async (objectTypeName: ObjectTypeName) => {
      setIsLoading(true);
      await onObjectNotInFunnelMapClick(objectTypeName);
      setIsLoading(false);
    },
    [onObjectNotInFunnelMapClick],
  );

  const onCardClick = useCallback(
    async (objectTypeName: ObjectTypeName, tab?: DocumentationTabTypes) => {
      const isUsedInFunnelMap = objectNamesUsedInCanvas?.includes(objectTypeName.objectType);

      if (isUsedInFunnelMap) {
        await onObjectInFunnelMapClick(objectTypeName, tab);
      } else {
        await _onAllObjectsCardClick(objectTypeName);
      }
    },
    [objectNamesUsedInCanvas, _onAllObjectsCardClick, onObjectInFunnelMapClick],
  );

  const backToObjectList = useCallback(() => {
    dispatchSingleObjectName({});
    dispatch(clearDependencies());
  }, [dispatch, dispatchSingleObjectName]);

  const backToElementsList = (event: any, el: { value: string; label: string }) => {
    dispatchSingleObjectName({
      singleObjectName: { label: el.label, objectType: el.value },
      tab: undefined,
    });
  };

  return {
    onCardClick,
    backToObjectList,
    backToElementsList,
    onObjectNotInFunnelMapClick,
    onObjectInFunnelMapClick,
    isLoading,
  };
};
